import React, { Suspense } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";

import { Loader2 } from "lucide-react";

import { useAuth } from "./AuthContext";
import { Login } from "./Login";
import { WelcomePage } from "./components/WelcomePage";

import { SpotifyProvider } from "./SpotifyContext";
import { PlaylistsProvider } from "./contexts/PlaylistsContext";
import { SongsProvider } from "./contexts/SongsContext";
import { SpotifyCallback } from "./SpotifyCallback";

import { ClientPortal } from "./ClientPortal";
import { PublicPresupuestoPage } from "./PublicPresupuestoPage";
import { LiveGuestPortal2 } from "./LiveGuestPortal2";
import DossierPublicView from "./components/DossierPublicView";

import { LazyPages } from "./utils/lazyPrefetch";
import AuthenticatedLayout from "./layouts/AuthenticatedLayout";
import AuthenticatedApp from "./pages/AuthenticatedApp";

// Destructuring de páginas lazy — colocado tras todos los imports para que analizadores
// estáticos (grep/AST) puedan identificar correctamente `LazyPages` como usado.
const {
  ReportsPage,
  CalendarPage,
  TasksPage,
  TrendsPage,
  TeamPage,
  StaffTimingPage,
  FinancesPage,
  PresupuestosPage,
  UsersPage,
  SettingsPage,
  PreciosPage,
  MP3LibraryPage,
  StoragePage,
  VenuesPage,
  PlantillasPage,
  DashboardPage,
  WizardHealthPage,
  AuditLogPage,
} = LazyPages;

// ============================================================================
// MainApp — solo la auth gate (loading / welcome / login).
// El cuerpo real de la app vive en <AuthenticatedApp /> (./pages/AuthenticatedApp.jsx).
// Esta separación elimina la clase entera de bug "Rules of Hooks" que tuvimos en iter79.
// ============================================================================
function MainApp() {
  const { loading, isAuthenticated } = useAuth();
  const location = useLocation();
  const [isStandalone, setIsStandalone] = React.useState(false);
  const [showWelcome, setShowWelcome] = React.useState(false);

  React.useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    const isPortalRoute = location.pathname.startsWith('/portal');
    const isCallbackRoute = location.pathname.includes('/callback');
    const isDossierRoute = location.pathname.startsWith('/dossier/');

    if (!standalone && !isPortalRoute && !isCallbackRoute && !isDossierRoute && !isAuthenticated && location.pathname === '/') {
      setShowWelcome(true);
    } else {
      setShowWelcome(false);
    }
  }, [location.pathname, isAuthenticated, loading]);

  const handleWelcomeDismiss = () => setShowWelcome(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  if (showWelcome && !isAuthenticated) {
    return <WelcomePage onDismiss={handleWelcomeDismiss} />;
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  // Suprimir warning de var no usada
  void isStandalone;

  return <AuthenticatedApp />;
}

function App() {
  const location = useLocation();

  // Check if we're on the client portal route
  if (location.pathname === '/portal' || location.pathname.startsWith('/portal/')) {
    return <ClientPortal />;
  }

  // Check if we're on the public presupuesto page
  if (location.pathname.startsWith('/presupuesto/')) {
    return <PublicPresupuestoPage />;
  }

  // Check if we're on the live guest portal route
  if (location.pathname.startsWith('/live/')) {
    return <LiveGuestPortal2 />;
  }

  return (
    <SpotifyProvider>
      <PlaylistsProvider>
        <SongsProvider>
          <Suspense fallback={
            <div className="flex items-center justify-center h-screen bg-black" data-testid="lazy-route-loader">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          }>
            <Routes>
              <Route path="/auth/spotify/callback" element={<SpotifyCallback />} />
              <Route path="/dossier/:token" element={<DossierRouteWrapper />} />

              {/* Rutas con layout compartido (cabecera incluida) */}
              <Route element={<AuthenticatedLayout />}>
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/trends" element={<TrendsPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/staff-timing" element={<StaffTimingPage />} />
                <Route path="/finances" element={<FinancesPage />} />
                <Route path="/presupuestos" element={<PresupuestosPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/precios" element={<PreciosPage />} />
                <Route path="/mp3-library" element={<MP3LibraryPage />} />
                <Route path="/almacenamiento" element={<StoragePage />} />
                <Route path="/venues" element={<VenuesPage />} />
                <Route path="/wizard-health" element={<WizardHealthPage />} />
                <Route path="/plantillas" element={<PlantillasPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/library" element={<LibraryRedirect />} />
                <Route path="/admin/audit-log" element={<AuditLogPage />} />
              </Route>

              {/* MainApp maneja el resto */}
              <Route path="/*" element={<MainApp />} />
            </Routes>
          </Suspense>
        </SongsProvider>
      </PlaylistsProvider>
    </SpotifyProvider>
  );
}

// LibraryRedirect - redirige /library a la vista interna de MainApp
function LibraryRedirect() {
  return <Navigate to="/" replace />;
}

// Wrapper para la ruta del dossier público
function DossierRouteWrapper() {
  const { token } = require('react-router-dom').useParams();
  return <DossierPublicView token={token} />;
}

export default App;
